import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Trash2, Loader2, Copy, CheckCircle, Check, ChevronDown, ExternalLink } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { UserAvatar } from '../components/UserAvatar';

const ALL_SCOPES = [
  { value: '*', label: 'Full Access', description: 'All permissions' },
  { value: 'workflows:read', label: 'Workflows: Read', description: 'List and view workflows' },
  { value: 'workflows:write', label: 'Workflows: Write', description: 'Create, update, test, duplicate' },
  { value: 'workflows:activate', label: 'Workflows: Activate', description: 'Activate and pause workflows' },
  { value: 'workflows:delete', label: 'Workflows: Delete', description: 'Delete workflows' },
  { value: 'executions:read', label: 'Executions: Read', description: 'View execution history' },
  { value: 'executions:cancel', label: 'Executions: Cancel', description: 'Cancel running executions' },
  { value: 'approvals:read', label: 'Approvals: Read', description: 'View pending approvals' },
  { value: 'approvals:manage', label: 'Approvals: Manage', description: 'Approve or reject gates' },
  { value: 'requests:read', label: 'Requests: Read', description: 'View workflow requests' },
  { value: 'requests:manage', label: 'Requests: Manage', description: 'Approve or reject requests' },
  { value: 'catalog:read', label: 'Catalog: Read', description: 'View signals, actions, integrations' },
];

// CommandBlock renders a single shell/code line with a copy button, matching the
// devtools styling used for the newly-created key display.
const CommandBlock: React.FC<{ command: string; language?: string }> = ({ command, language }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="group relative">
      <pre className="text-xs font-mono bg-background border rounded-md px-3 py-2 pr-10 overflow-x-auto whitespace-pre">
        <code>{command}</code>
      </pre>
      <button
        type="button"
        aria-label="Copy command"
        onClick={() => {
          navigator.clipboard.writeText(command);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="absolute right-1.5 top-1.5 h-6 w-6 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      {language && (
        <span className="absolute right-9 top-2 text-[10px] uppercase tracking-wide text-muted-foreground/60 select-none">
          {language}
        </span>
      )}
    </div>
  );
};

type ToolTab = 'cli' | 'mcp' | 'sdk';

const TOOL_TABS: { id: ToolTab; label: string; docs: string }[] = [
  { id: 'cli', label: 'CLI', docs: 'https://docs.usekestrel.ai/workflows/cli' },
  { id: 'mcp', label: 'MCP', docs: 'https://docs.usekestrel.ai/workflows/mcp' },
  { id: 'sdk', label: 'SDK', docs: 'https://docs.usekestrel.ai/workflows/sdk' },
];

const APIKeys: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'ops_admin';
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('never');
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(['*']);
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [error, setError] = useState('');
  const [allowedScopes, setAllowedScopes] = useState<string[]>(['*']);
  const [activeTool, setActiveTool] = useState<ToolTab>('cli');

  useEffect(() => { fetchKeys(); }, []);

  useEffect(() => {
    if (!user || user.role === 'org_admin') return;
    const fetchConfig = async () => {
      try {
        const configs = await apiClient.getRoleAccessConfig();
        const myConfig = configs[user.role];
        if (myConfig?.api_key_scopes?.length > 0) {
          setAllowedScopes(myConfig.api_key_scopes);
          if (!myConfig.api_key_scopes.includes('*')) {
            setNewKeyScopes(myConfig.api_key_scopes);
          }
        }
      } catch { /* use defaults */ }
    };
    fetchConfig();
  }, [user]);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      setApiKeys(await apiClient.listApiKeys());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) {
      setError('Enter a key name first');
      return;
    }
    if (newKeyScopes.length === 0) {
      setError('Select at least one scope');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const data = await apiClient.createApiKey({
        name: newKeyName.trim(),
        scopes: newKeyScopes,
        expires_in: newKeyExpiry,
      });
      setNewlyCreatedKey(data.raw_key);
      setNewKeyName('');
      setNewKeyExpiry('never');
      setNewKeyScopes(['*']);
      fetchKeys();
    } catch (err: any) {
      console.error('Create API key failed:', err);
      setError(err?.response?.data || err?.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (keyId: string) => {
    await apiClient.revokeApiKey(keyId).catch(() => {});
    fetchKeys();
  };

  const deleteKey = async (keyId: string) => {
    await apiClient.deleteApiKey(keyId).catch(() => {});
    fetchKeys();
  };

  const getStatus = (key: any) => {
    if (key.revoked_at) return 'revoked';
    if (key.expires_at && new Date(key.expires_at) < new Date()) return 'expired';
    return 'active';
  };

  const toggleScope = (scope: string) => {
    if (scope === '*') {
      setNewKeyScopes(['*']);
      return;
    }
    setNewKeyScopes(prev => {
      const without = prev.filter(s => s !== '*');
      if (without.includes(scope)) {
        return without.filter(s => s !== scope);
      }
      return [...without, scope];
    });
  };

  const scopeLabel = newKeyScopes.includes('*') && allowedScopes.includes('*')
    ? 'Full Access'
    : `${newKeyScopes.length} scope${newKeyScopes.length !== 1 ? 's' : ''}`;

  const formatScopes = (scopes: string[]) => {
    if (!scopes || scopes.length === 0) return '—';
    if (scopes.includes('*')) return 'Full Access';
    return scopes.join(', ');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[calc(100vh-140px)]"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-md border border-destructive/30 bg-destructive/5 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Create bar */}
      <Card className="p-3">
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
          <Input
            placeholder="Key name (e.g., ci-deploy, monitoring)"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            className="h-8 text-sm max-w-[250px]"
            onKeyDown={e => { if (e.key === 'Enter') createKey(); }}
            autoFocus
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1 font-normal">
                {scopeLabel}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <div className="max-h-[300px] overflow-y-auto p-1">
                {ALL_SCOPES.filter(s => allowedScopes.includes('*') || allowedScopes.includes(s.value)).map(s => {
                  const selected = s.value === '*' ? newKeyScopes.includes('*') : newKeyScopes.includes(s.value);
                  return (
                    <button
                      key={s.value}
                      className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${selected ? 'bg-accent/50' : ''}`}
                      onClick={() => toggleScope(s.value)}
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                        {selected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                      </div>
                      <div>
                        <div className="font-medium">{s.label}</div>
                        <div className="text-muted-foreground">{s.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1 font-normal">
                {newKeyExpiry === 'never' ? 'No expiration' : newKeyExpiry === '30d' ? '30 days' : newKeyExpiry === '90d' ? '90 days' : '1 year'}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[160px] p-1" align="start">
              {[
                { value: 'never', label: 'No expiration' },
                { value: '30d', label: '30 days' },
                { value: '90d', label: '90 days' },
                { value: '365d', label: '1 year' },
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${newKeyExpiry === opt.value ? 'bg-accent/50' : ''}`}
                  onClick={() => setNewKeyExpiry(opt.value)}
                >
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${newKeyExpiry === opt.value ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                    {newKeyExpiry === opt.value && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                  </div>
                  {opt.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Button onClick={createKey} disabled={creating} size="sm" className="h-8 gap-1.5">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create Key
          </Button>
        </div>
      </Card>

      {/* Newly created key */}
      {newlyCreatedKey && (
        <Card className="p-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Your new API key — copy it now, it won't be shown again:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm font-mono bg-background border rounded px-3 py-2 break-all select-all">
              {newlyCreatedKey}
            </code>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 shrink-0"
              onClick={() => {
                navigator.clipboard.writeText(newlyCreatedKey);
                setCopiedKey(true);
                setTimeout(() => setCopiedKey(false), 2000);
              }}
            >
              {copiedKey ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedKey ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground" onClick={() => setNewlyCreatedKey(null)}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card>
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                {isAdmin && <TableHead>Created By</TableHead>}
                <TableHead>Scopes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12 text-muted-foreground">
                    No API keys yet. Create one above to get started.
                  </TableCell>
                </TableRow>
              ) : (
                apiKeys.map((key: any) => {
                  const status = getStatus(key);
                  return (
                    <TableRow key={key.id}>
                      <TableCell>
                        <span className="font-medium text-sm">{key.name}</span>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs font-mono text-muted-foreground">{key.key_prefix}...</code>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <UserAvatar
                              email={key.created_by_email || ''}
                              pictureUrl={key.created_by_picture}
                            />
                            <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                              {key.created_by_email || '—'}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-muted-foreground">
                        {key.scopes?.includes('*') ? (
                          <span>Full Access</span>
                        ) : key.scopes?.length > 0 ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="text-left hover:underline underline-offset-2">
                                {key.scopes.length} scope{key.scopes.length !== 1 ? 's' : ''}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[220px] p-2" align="start">
                              <div className="space-y-1">
                                {key.scopes.map((s: string) => (
                                  <div key={s} className="text-xs font-mono text-muted-foreground">{s}</div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={status === 'active' ? 'default' : 'destructive'}
                          className={`text-xs ${status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30' : ''}`}
                        >
                          {status === 'active' ? 'Active' : status === 'revoked' ? 'Revoked' : 'Expired'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(key.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {status === 'active' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-orange-600">
                                  Revoke
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will immediately revoke <strong>{key.name}</strong>. Any application using this key will lose access. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => revokeKey(key.id)}>Revoke Key</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 px-1.5 text-muted-foreground hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete API Key</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete <strong>{key.name}</strong>. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteKey(key.id)}>Delete Key</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Programmatic access: CLI / MCP / SDK */}
      <Card className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold">Use your key with the CLI, MCP &amp; SDK</h3>
            <p className="text-xs text-muted-foreground">Manage workflows programmatically. Replace <code className="font-mono">kestrel_sk_…</code> with a key from above.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-3 border-b">
          {TOOL_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTool(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium -mb-px border-b-2 transition-colors ${
                activeTool === tab.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
          <a
            href={TOOL_TABS.find(t => t.id === activeTool)?.docs}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors pb-1.5"
          >
            Docs <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* CLI */}
        {activeTool === 'cli' && (
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">1. Install (Homebrew)</p>
              <CommandBlock command="brew install KestrelAI/tap/kestrel" language="bash" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">2. Authenticate with your API key</p>
              <CommandBlock command="kestrel auth kestrel_sk_..." language="bash" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">3. Verify & start managing workflows</p>
              <CommandBlock command={'kestrel status\nkestrel workflows list'} language="bash" />
            </div>
          </div>
        )}

        {/* MCP */}
        {activeTool === 'mcp' && (
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">1. Install &amp; authenticate the CLI (the MCP server uses its credentials)</p>
              <CommandBlock command={'brew install KestrelAI/tap/kestrel\nkestrel auth kestrel_sk_...'} language="bash" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">2. Add the MCP server to your AI tool</p>
              <p className="text-[11px] text-muted-foreground mb-1.5">Cursor — add to <code className="font-mono">~/.cursor/mcp.json</code></p>
              <CommandBlock command={'{\n  "mcpServers": {\n    "kestrel": {\n      "command": "kestrel",\n      "args": ["mcp"]\n    }\n  }\n}'} language="json" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground mb-1.5">Claude Code — add via the CLI</p>
              <CommandBlock command="claude mcp add kestrel -- kestrel mcp" language="bash" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground mb-1.5">Codex CLI — add via the CLI</p>
              <CommandBlock command="codex mcp add kestrel -- kestrel mcp" language="bash" />
            </div>
          </div>
        )}

        {/* SDK */}
        {activeTool === 'sdk' && (
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">1. Install (Python)</p>
              <CommandBlock command="pip install kestrel-workflows" language="bash" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">2. Authenticate with your API key</p>
              <CommandBlock command={'from kestrel import KestrelClient\n\nclient = KestrelClient(api_key="kestrel_sk_...")\nworkflows = client.workflows.list()'} language="python" />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default APIKeys;
